import type Hls from 'hls.js';

/**
 * The viewer's quality choice. Streams list two qualities (index.m3u8 → hi.m3u8, the stream as
 * sent, and lo.m3u8, a lighter 360p copy); "auto" lets hls.js pick from how fast segments load.
 */
export type QualityChoice = 'auto' | 'high' | 'low';

const KEY = 'z2l.quality';

/** The viewer's last choice on this browser (a phone on metered data keeps "low"). */
export function savedQuality(): QualityChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'high' || v === 'low' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

export function saveQuality(c: QualityChoice) {
  try {
    if (c === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, c);
  } catch {
    /* private mode or storage blocked: the choice lasts for this page only */
  }
}

/** Where each quality is in hls.levels, or null when the stream has only one (older streams, OBS at 1080p). */
export function qualityLevels(hls: Hls): { high: number; low: number } | null {
  const find = (name: string) => hls.levels.findIndex((l) => new RegExp(`/${name}\\.m3u8(\\?|$)`).test(l.uri));
  const high = find('hi');
  const low = find('lo');
  return high >= 0 && low >= 0 ? { high, low } : null;
}

/**
 * Pin a quality (switching at the next segment, without emptying the buffer), or go back to auto.
 * Called before playback starts too (Player, on MANIFEST_PARSED), so a saved choice also picks the
 * first segment.
 */
export function applyQuality(hls: Hls, c: QualityChoice) {
  const levels = qualityLevels(hls);
  if (!levels) return;
  const level = c === 'auto' ? -1 : c === 'high' ? levels.high : levels.low;
  hls.startLevel = level;
  hls.nextLevel = level;
}
